import Features from '@dashboard/enums/features';

import { canUseFeature, features as featureMatrix } from './features';

// Sentinel for event staff: gated off Free outside features.js (enforced in
// InviteStaffWizard / event-staff code), shown as a paid-plan feature.
export const EVENT_STAFF = 'eventStaff';

// Flat, ordered list of what a paid plan unlocks vs Free. Each item maps to the
// Features.* value it gates on (or EVENT_STAFF) and the tier it enters at.
// This is display order; the Launch card renders launchFeatureItems in this order.
// The colocated test keeps it in sync with features.js (coverage + tier).
export const paidPlanFeatureItems = [
  {
    label: 'Standard support',
    feature: Features.SEATS,
    tier: 'launch',
    tooltip: 'Chat & email support',
  },
  {
    label: 'Unlimited team members',
    feature: Features.SEATS,
    tier: 'launch',
    tooltip: 'Invite your whole team to manage your store together.',
  },
  {
    label: 'Remove Checkout Page branding',
    feature: Features.CHECKOUT_PAGE_BADGE,
    tier: 'launch',
    tooltip: 'Remove the Powered by Checkout Page badge from your store.',
  },
  {
    label: 'Email customers',
    feature: Features.EMAIL_CUSTOMER,
    tier: 'launch',
    tooltip: 'Send a one-off email to a customer from their payment, booking, subscription or submission.',
  },
  {
    label: 'Custom domain',
    feature: Features.CUSTOM_DOMAIN,
    tier: 'scale',
    tooltip: 'Add your domain name to your checkout pages, events, and forms.',
  },
  {
    label: 'Custom email domain',
    feature: Features.EMAIL_DOMAIN,
    tier: 'scale',
    tooltip: 'Send confirmation emails from your own email address.',
  },
  {
    label: 'One-click upsells',
    feature: Features.ONE_CLICK_UPSELLS,
    tier: 'scale',
    tooltip: 'Offer add-on products customers can buy in one click after checkout.',
  },
  {
    label: 'Cart abandonment recovery',
    feature: Features.CART_ABANDONMENT,
    tier: 'scale',
    tooltip: 'Automatically send up to 3 reminder emails when a checkout is abandoned.',
  },
  {
    label: 'Manual payments',
    feature: Features.MANUAL_PAYMENTS,
    tier: 'scale',
    tooltip: 'Let customers pay manually, like by bank transfer or invoice.',
  },
  {
    label: 'Bulk discounts',
    feature: Features.BULK_DISCOUNTS,
    tier: 'launch',
    tooltip: 'Generate and manage discount codes in bulk.',
  },
  {
    label: 'Custom fees',
    feature: Features.CUSTOM_FEES,
    tier: 'launch',
    tooltip: 'Add extra fees at checkout, like processing or handling.',
  },
  {
    label: 'Dynamic pricing',
    feature: Features.DYNAMIC_PRICING,
    tier: 'launch',
    tooltip: 'Dynamically change the price and other details via the checkout URL.',
  },
  {
    label: 'Redirect to URL',
    feature: Features.REDIRECT_TO_URL,
    tier: 'launch',
    tooltip: 'Send customers to a custom URL after they pay.',
  },
  {
    label: 'Event staff',
    feature: EVENT_STAFF,
    tier: 'launch',
    tooltip: 'Invite staff to check in guests at your event.',
  },
  {
    label: 'Tracking pixels',
    feature: Features.TRACKING_PIXELS,
    tier: 'launch',
    tooltip: 'Add tracking codes from Google Analytics, Facebook Pixel and others.',
  },
  {
    label: 'Custom CSS',
    feature: Features.CUSTOM_CSS,
    tier: 'launch',
    tooltip: 'Fine-tune the look of your pages with custom CSS.',
  },
];

export const launchFeatureItems = paidPlanFeatureItems.filter(item => item.tier === 'launch');

// Features gated off Free that we intentionally do NOT surface (too granular).
export const INTENTIONALLY_OMITTED = [Features.OPTIONAL_EMAIL_CONFIRMATION, Features.NOTIFY_EMAIL];

// Throws at import time on a typo, so the colocated test fails loudly.
function paidItem(label) {
  const item = paidPlanFeatureItems.find(i => i.label === label);
  if (!item) throw new Error(`Unknown paid plan feature label: ${label}`);
  return { label: item.label, tooltip: item.tooltip, feature: item.feature, included: true };
}

function excluded(label) {
  return { ...paidItem(label), included: false };
}

// Card items carry a static `included` baseline for new customers. Features with a
// grandfather clause are re-evaluated per seller so grandfathered sellers see the
// features they keep as included rather than locked; other items pass through.
export const resolveCardItemIncluded = (item, planHandle, subscribedAt) => {
  if (item.feature && featureMatrix[item.feature]?.grandfather) {
    return canUseFeature(item.feature, planHandle, { subscribedAt });
  }
  return item.included;
};

// What each plan card shows on /upgrade, grouped with section headings.
// Items with included: false render grayed out, so each tier also shows what
// it does NOT unlock. Non-gated marketing items (support levels, Free basics)
// are declared inline; everything else references paidPlanFeatureItems so the
// drift tests keep the cards in sync with features.js.
export const planCardFeatureGroups = {
  free: {
    groups: [
      {
        heading: 'Support & team',
        items: [
          {
            label: 'Essential support',
            tooltip: 'Documentation and email support',
            included: true,
          },
          {
            label: '1 team member',
            tooltip: 'Manage your store with a single account. Upgrade to invite your team.',
            included: true,
          },
          excluded('Unlimited team members'),
        ],
      },
      {
        heading: 'Checkout & customization',
        items: [
          { label: 'Unlimited checkout pages', included: true },
          { label: 'Unlimited events', included: true },
          { label: 'Unlimited forms', included: true },
          {
            label: 'Checkout Page branding',
            tooltip: 'Your store shows Checkout Page branding. Upgrade to remove it.',
            included: true,
          },
          excluded('Remove Checkout Page branding'),
          excluded('Custom CSS'),
          excluded('Email customers'),
        ],
      },
      {
        heading: 'Revenue boosters',
        items: [
          excluded('Bulk discounts'),
          excluded('Custom fees'),
          excluded('Dynamic pricing'),
          excluded('One-click upsells'),
          excluded('Cart abandonment recovery'),
          excluded('Manual payments'),
        ],
      },
      {
        heading: 'Domains & integrations',
        items: [
          excluded('Tracking pixels'),
          excluded('Custom domain'),
          excluded('Custom email domain'),
        ],
      },
    ],
  },
  launch: {
    groups: [
      {
        heading: 'Support & team',
        items: [
          paidItem('Standard support'),
          paidItem('Unlimited team members'),
          paidItem('Event staff'),
        ],
      },
      {
        heading: 'Checkout & customization',
        items: [
          paidItem('Remove Checkout Page branding'),
          paidItem('Redirect to URL'),
          paidItem('Custom CSS'),
          paidItem('Email customers'),
        ],
      },
      {
        heading: 'Revenue boosters',
        items: [
          paidItem('Bulk discounts'),
          paidItem('Custom fees'),
          paidItem('Dynamic pricing'),
          excluded('One-click upsells'),
          excluded('Cart abandonment recovery'),
          excluded('Manual payments'),
        ],
      },
      {
        heading: 'Domains & integrations',
        items: [
          paidItem('Tracking pixels'),
          excluded('Custom domain'),
          excluded('Custom email domain'),
        ],
      },
    ],
  },
  scale: {
    groups: [
      {
        heading: 'Support & team',
        items: [
          { label: 'Premium support', tooltip: 'Priority chat & email support', included: true },
        ],
      },
      {
        heading: 'Revenue boosters',
        items: [
          paidItem('One-click upsells'),
          paidItem('Cart abandonment recovery'),
          paidItem('Manual payments'),
        ],
      },
      {
        heading: 'Domains & integrations',
        items: [paidItem('Custom domain'), paidItem('Custom email domain')],
      },
    ],
  },
  unlimited: {
    groups: [
      {
        heading: 'Sales',
        items: [
          {
            label: 'Unlimited sales',
            tooltip: 'No sales limit and no overage fees.',
            included: true,
          },
        ],
      },
      {
        heading: 'Support & team',
        items: [
          {
            label: 'Dedicated support',
            tooltip: 'Reach out to the founders directly',
            included: true,
          },
        ],
      },
      {
        heading: 'Onboarding',
        items: [
          {
            label: 'Personalized onboarding',
            tooltip: 'Get hands-on setup help from our team.',
            included: true,
          },
          {
            label: 'Free migration',
            tooltip: "We'll move your existing pages and products over for you.",
            included: true,
          },
        ],
      },
    ],
  },
};
